import { SchemaFieldType, SchemaFieldTypeExtended } from "config/config";
import { operatorsList } from "./constants";

export const getMatchingSchema = (allFields: SchemaFieldTypeExtended[], value: string) =>
    allFields?.find((matchingField) => matchingField.name === value);

export const eqTrueOp = "eqTrue";
export const eqFalseOp = "eqFalse";
export const isBooleanTypeOperator = (op) => op === eqTrueOp || op === eqFalseOp;
export const isNullableOperator = (op) => op === "set" || op === "unset";

export const getOperatorList = (matchingSchema?: SchemaFieldType) => {
    const getOpList = (possibleValues) =>
        operatorsList.filter((op) => possibleValues.includes(op.value));

    let opList: { value: string; label: string }[] = [];
    switch (matchingSchema?.type) {
        case "bool":
            opList = [
                { value: eqTrueOp, label: "True" },
                { value: eqFalseOp, label: "False" },
            ];
            break;
        case "str":
            if (matchingSchema.foreign_keys.length) {
                opList = getOpList(["eq", "neq", "in", "notin"]);
            } else if (matchingSchema.schema?.["format"] === "uuid") {
                opList = getOpList(["eq", "neq"]);
            } else if (Array.isArray(matchingSchema.schema?.["enum"])) {
                // Enum-backed strings (ALLOWED_VALUES object-template attributes such as
                // operator-task.status) are a fixed picklist, so offer exact match plus
                // multi-value membership. The enum value control (ValueField) already
                // renders a multi-select for the `in`/`notin` operators.
                opList = getOpList(["eq", "neq", "in", "notin"]);
            } else {
                // Plain free-text strings (no foreign key, no enum picklist) support
                // substring/exact match plus multi-value membership. ValueField renders a
                // multi-value tag input for the `in`/`notin` operators.
                opList = getOpList(["like", "notlike", "eq", "neq", "in", "notin"]);
            }
            break;
        case "datetime":
            opList = [
                { value: "eq", label: "Is" },
                { value: "gt", label: "After" },
                { value: "lt", label: "Before" },
            ];
            break;
        case "int":
            opList = getOpList(["lt", "lte", "gt", "gte", "eq", "neq", "in", "notin"]);
            break;
        case "dict":
            opList = getOpList(["eq", "neq"]);
            break;
        case "list":
            opList = getOpList(["in", "notin"]);
            break;
        default:
            if (
                matchingSchema?.type &&
                (/Type$/.test(matchingSchema.type) ||
                    /Status$/.test(matchingSchema.type) ||
                    /ProvisioningState$/.test(matchingSchema.type))
            ) {
                opList = getOpList(["eq", "neq", "in", "notin"]);
            }
            break;
    }
    if (matchingSchema?.nullable) {
        const nullOp = getOpList(["set", "unset"]);
        opList.push(...nullOp);
    }
    return opList;
};

export const isArrayOperator = (operator: string) => operator === "in" || operator === "notin";

export interface ReferenceFilterClause {
    field: string;
    op: string;
    value?: unknown;
}

export interface ReferenceValueOptions {
    resource: string;
    resourceBody: string;
    // A single clause (the common case: scope CO candidates to one object_template),
    // or an `and`-group of clauses when extra restrictions apply (e.g. also require a
    // back-reference attribute to be unset). The Autocomplete service pushes this whole
    // value into its outer `{and:[…]}`, so a nested `and`-group composes correctly.
    queryField?: ReferenceFilterClause | { and: ReferenceFilterClause[] };
}

/**
 * Build the resource-backed Autocomplete options for a record-reference filter *value*
 * control (defect 2 of operator-task-advanced-search-…-uuid-value-picker). `resource`
 * is the lowercased referenced record_type (e.g. USER -> "user", CUSTOM_OBJECT ->
 * "custom_object"); a CUSTOM_OBJECT reference additionally scopes the option list to the
 * referenced object_template_id via a server-side queryField, mirroring
 * DefaultValueEditor's reference control. Kept as a pure helper so the resource/scope
 * mapping is unit-testable without rendering the async picker.
 *
 * `requireUnsetValueAttribute` (CUSTOM_OBJECT only) additionally restricts candidates to
 * records whose `value.<attribute>` is currently null/unset. The incoming-reference
 * link-existing picker passes the back-reference attribute here so it lists only records
 * that are NOT already linked to another parent — selecting one then sets its FK without
 * silently re-parenting (stealing) a record from its current owner (Stefan 2026-10-13).
 * The null clause is AND'd with the object_template scope in a single `and`-group; the
 * backend rejects a bare `value.*` filter unless the object_template_id is AND'd in the
 * same group, so the two always travel together.
 */
export const buildReferenceValueOptions = (
    referenceRecordType: string,
    referenceObjectTemplateId?: unknown,
    requireUnsetValueAttribute?: string
): ReferenceValueOptions => {
    const resource = referenceRecordType.toLowerCase();
    const resourceBody =
        resource === "project" ||
        resource === "bundle" ||
        resource === "workspace" ||
        resource === "user"
            ? "icon"
            : "";
    if (resource === "custom_object" && typeof referenceObjectTemplateId === "string") {
        const templateClause: ReferenceFilterClause = {
            field: "object_template_id",
            op: "eq",
            value: referenceObjectTemplateId,
        };
        if (requireUnsetValueAttribute) {
            return {
                resource,
                resourceBody,
                queryField: {
                    and: [
                        templateClause,
                        { field: `value.${requireUnsetValueAttribute}`, op: "unset", value: null },
                    ],
                },
            };
        }
        return { resource, resourceBody, queryField: templateClause };
    }
    return { resource, resourceBody };
};
